/**
 *
 * @module mediaDescriptor
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/playback-mapping.md
 *
 */

import Check from 'check-types';
import Exception from './../services/exception/entry';
import AdInsertionStrategy from './adInsertionStrategy';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    includes,
    nonEmptyString,
    object,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class MediaDescriptor
 * @desc A description of an item available from the media service.
 *
 */
export default class MediaDescriptor {

    /**
     *
     * @param {Object} options
     * @param {String} options.mediaLocation - The absolute URL for the media service request.
     * @param {AdInsertionStrategy} [options.adInsertionStrategy=AdInsertionStrategy.NONE] - The advertising
     * insertion strategy to be used with the media.
     * @param {String} [options.basePlaybackScenario=null] - The playback scenario key that will be used to convey
     * additional information about what variant of media that is desired.
     * @param {Object} [options.adTargeting={}]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'MediaDescriptor(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get adInsertionStrategy() {
                return `${this.ctor} needs a valid AdInsertionStrategy`;
            },
            get mediaLocation() {
                return `${this.ctor} options.mediaLocation should be String with length`;
            },
            get basePlaybackScenario() {
                return `${this.ctor} options.basePlaybackScenario should be a String with length`;
            },
            get adTargeting() {
                return `${this.ctor} options.adTargeting should be an Object`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { mediaLocation, adInsertionStrategy = AdInsertionStrategy.NONE, basePlaybackScenario = null } = options;
        const { adTargeting = {} } = options;

        nonEmptyString(mediaLocation, errorMessage.mediaLocation, InvalidArgumentException);
        includes(AdInsertionStrategy, adInsertionStrategy, errorMessage.AdInsertionStrategy, InvalidArgumentException);

        if (Check.assigned(basePlaybackScenario)) {
            nonEmptyString(basePlaybackScenario, errorMessage.basePlaybackScenario, InvalidArgumentException);
        }

        if (Check.assigned(adTargeting)) {
            object(adTargeting, errorMessage.adTargeting, InvalidArgumentException);
        }

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets the absolute URL for the media service request.
         *
         */
        this.mediaLocation = mediaLocation;

        /**
         *
         * @access public
         * @type {AdInsertionStrategy}
         * @desc Gets or sets the advertising insertion strategy to be used with the media.
         * Default is AdInsertionStrategy.None
         *
         */
        this.adInsertionStrategy = adInsertionStrategy;

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the playback scenario key used to convey additional information
         * about what variant of media that is desired. Default is null.
         *
         */
        this.basePlaybackScenario = basePlaybackScenario;

        /**
         *
         * @access public
         * @type {Object}
         * @desc Custom adTargeting object to be used when setting cookies related to AdEngine
         *
         */
        this.adTargeting = adTargeting;

    }

}



// WEBPACK FOOTER //
// ./src/media/mediaDescriptor.js